import React from 'react';
import { useAnalyticsContext } from './AnalyticsProvider';
import { getAnalyticsSettings } from '@/config/analytics';
import { BarChart3, Users, Eye, Clock, Smartphone, Monitor } from 'lucide-react';

interface MetricCardProps {
  title: string;
  value: string | number;
  icon: React.ReactNode;
  description?: string;
  status?: 'success' | 'warning' | 'error' | 'info';
}

const MetricCard: React.FC<MetricCardProps> = ({ 
  title, 
  value, 
  icon, 
  description, 
  status = 'info' 
}) => {
  const statusColors = {
    success: 'border-green-200 bg-green-50 text-green-800',
    warning: 'border-yellow-200 bg-yellow-50 text-yellow-800',
    error: 'border-red-200 bg-red-50 text-red-800',
    info: 'border-blue-200 bg-blue-50 text-blue-800'
  };

  return (
    <div className={`p-6 rounded-lg border ${statusColors[status]}`}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium opacity-80">{title}</p>
          <p className="text-2xl font-bold">{value}</p>
          {description && (
            <p className="text-sm opacity-70 mt-1">{description}</p>
          )}
        </div>
        <div className="text-3xl opacity-60">
          {icon}
        </div>
      </div>
    </div>
  );
};

export const AnalyticsDashboard: React.FC = () => {
  const { isAnalyticsLoaded, isAnalyticsConfigured, shouldTrack } = useAnalyticsContext();
  const { measurementId, debugMode } = getAnalyticsSettings();

  const getStatusColor = () => {
    if (!isAnalyticsConfigured) return 'error';
    if (!isAnalyticsLoaded) return 'warning';
    if (!shouldTrack) return 'warning';
    return 'success';
  };

  const getStatusText = () => {
    if (!isAnalyticsConfigured) return 'No configurado';
    if (!isAnalyticsLoaded) return 'Cargando...';
    if (!shouldTrack) return 'Deshabilitado';
    return 'Activo';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-foreground flex items-center gap-2">
            <BarChart3 className="h-6 w-6 text-primary" />
            Analytics Dashboard
          </h2>
          <p className="text-muted-foreground mt-1">
            Monitoreo de métricas y comportamiento de usuarios
          </p>
        </div>
        
        {/* Status Badge */}
        <div className={`px-3 py-1 rounded-full text-sm font-medium ${
          getStatusColor() === 'success' ? 'bg-green-100 text-green-800' :
          getStatusColor() === 'warning' ? 'bg-yellow-100 text-yellow-800' :
          getStatusColor() === 'error' ? 'bg-red-100 text-red-800' :
          'bg-blue-100 text-blue-800'
        }`}>
          {getStatusText()}
        </div>
      </div>

      {/* Configuration Status */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <MetricCard
          title="Estado de GA4"
          value={getStatusText()}
          icon={<BarChart3 />}
          description="Google Analytics 4"
          status={getStatusColor()}
        />
        
        <MetricCard
          title="Measurement ID"
          value={measurementId || 'No configurado'}
          icon={<Eye />}
          description="ID de seguimiento"
          status={isAnalyticsConfigured ? 'success' : 'error'}
        />
        
        <MetricCard
          title="Modo Debug"
          value={debugMode ? 'Activado' : 'Desactivado'}
          icon={<Monitor />}
          description="Logs de desarrollo"
          status={debugMode ? 'info' : 'success'}
        />
      </div>

      {/* Metrics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          title="Usuarios Únicos"
          value="--"
          icon={<Users />}
          description="Hoy"
          status="info"
        />
        
        <MetricCard
          title="Page Views"
          value="--"
          icon={<Eye />}
          description="Hoy"
          status="info"
        />
        
        <MetricCard
          title="Tiempo Promedio"
          value="--"
          icon={<Clock />}
          description="Por sesión"
          status="info"
        />
        
        <MetricCard
          title="Dispositivos"
          value="--"
          icon={<Smartphone />}
          description="Mobile vs Desktop"
          status="info"
        />
      </div>

      {/* Recent Activity */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">
          📊 Actividad Reciente
        </h3>
        <div className="space-y-3">
          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <div className="flex items-center gap-3">
              <div className="w-2 h-2 bg-green-500 rounded-full"></div>
              <span className="text-sm text-gray-600">Analytics activo y funcionando</span>
            </div>
            <span className="text-xs text-gray-500">Ahora</span>
          </div>
          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <div className="flex items-center gap-3">
              <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
              <span className="text-sm text-gray-600">Tracking de eventos implementado</span>
            </div>
            <span className="text-xs text-gray-500">Hoy</span>
          </div>
          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <div className="flex items-center gap-3">
              <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
              <span className="text-sm text-gray-600">Dashboard de analytics creado</span>
            </div>
            <span className="text-xs text-gray-500">Hoy</span>
          </div>
        </div>
      </div>

      {/* Configuration Instructions */}
      {!isAnalyticsConfigured && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-yellow-800 mb-3">
            🚨 Configuración Requerida
          </h3>
          <div className="text-yellow-700 space-y-2">
            <p>Para activar Google Analytics 4:</p>
            <ol className="list-decimal list-inside space-y-1 ml-4">
              <li>Ve a <a href="https://analytics.google.com" target="_blank" rel="noopener noreferrer" className="underline">analytics.google.com</a></li>
              <li>Crea una cuenta y propiedad para Statemia</li>
              <li>Copia el Measurement ID (G-XXXXXXXXXX)</li>
              <li>Agrega a tu archivo <code className="bg-yellow-100 px-1 rounded">.env.local</code>:</li>
            </ol>
            <div className="bg-yellow-100 p-3 rounded font-mono text-sm mt-3">
              VITE_GA4_MEASUREMENT_ID=G-XXXXXXXXXX<br />
              VITE_GA4_DEBUG_MODE=true
            </div>
          </div>
        </div>
      )}

      {/* Debug Information */}
      {debugMode && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-3">
            🔧 Información de Debug
          </h3>
          <div className="space-y-2 text-sm text-gray-600">
            <p><strong>Analytics Configurado:</strong> {isAnalyticsConfigured ? 'Sí' : 'No'}</p>
            <p><strong>Analytics Cargado:</strong> {isAnalyticsLoaded ? 'Sí' : 'No'}</p>
            <p><strong>Tracking Habilitado:</strong> {shouldTrack ? 'Sí' : 'No'}</p>
            <p><strong>Measurement ID:</strong> {measurementId || 'No configurado'}</p>
            <p><strong>Modo Debug:</strong> {debugMode ? 'Activado' : 'Desactivado'}</p>
            <p><strong>Entorno:</strong> {import.meta.env.MODE}</p>
          </div>
        </div>
      )}

      {/* Next Steps */}
      {isAnalyticsConfigured && isAnalyticsLoaded && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-green-800 mb-3">
            ✅ ¡Analytics Configurado Correctamente!
          </h3>
          <div className="text-green-700 space-y-2">
            <p>Google Analytics 4 está funcionando. Próximos pasos:</p>
            <ul className="list-disc list-inside space-y-1 ml-4">
              <li>Revisa tu dashboard de GA4 para ver datos en tiempo real</li>
              <li>Configura eventos personalizados para métricas específicas</li>
              <li>Implementa tracking de conversiones y objetivos</li>
              <li>Configura alertas y reportes automáticos</li>
            </ul>
          </div>
        </div>
      )}

      {/* Tracking Implementation Status */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-blue-800 mb-4">
          🎯 Estado del Tracking Implementado
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-3">
            <h4 className="font-medium text-blue-700">✅ Páginas con Tracking:</h4>
            <ul className="space-y-2 text-sm text-blue-600">
              <li className="flex items-center gap-2">
                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                <span>NewsLayout (Landing)</span>
              </li>
              <li className="flex items-center gap-2">
                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                <span>StatisticsList (Lista)</span>
              </li>
              <li className="flex items-center gap-2">
                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                <span>StatisticViewer (Artículos)</span>
              </li>
            </ul>
          </div>
          <div className="space-y-3">
            <h4 className="font-medium text-blue-700">📊 Eventos Trackeados:</h4>
            <ul className="space-y-2 text-sm text-blue-600">
              <li className="flex items-center gap-2">
                <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                <span>Vistas de artículos</span>
              </li>
              <li className="flex items-center gap-2">
                <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                <span>Búsquedas y filtros</span>
              </li>
              <li className="flex items-center gap-2">
                <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                <span>Interacciones de usuario</span>
              </li>
              <li className="flex items-center gap-2">
                <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                <span>Navegación y paginación</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}; 